/*
 * ConversationPane — the reading pane in conversation mode (ADR 0077,
 * docs/plan/16-conversation-view.md §8).
 *
 * The opened message's conversation as a stack, oldest first: collapsed
 * messages are one line (sender, snippet, date), expanded ones are the flat
 * pane's header, body and attachments (`MessageBodySection`, shared with
 * `ReadingPane`). The toolbar acts on the conversation; each message has its
 * own Reply / Reply all / Forward and ⋮; Reply at the bottom answers the
 * newest message.
 *
 * ## Props, and how the mail view wires them
 *
 * - `accountId`, `messageId` — the opened message. It is expanded and
 *   scrolled into view; the conversation comes from `useConversation`.
 *
 * ## Long conversations
 *
 * More than `SHORT_STACK` messages draw as Gmail does (`stackEntries`): the
 * oldest, one "N older messages" divider for the middle, and the newest
 * `TAIL`. What must be seen is never behind a divider — the unread, the
 * opened and the expanded messages split it around themselves. A divider
 * reveals `REVEAL_BATCH` messages per click, and what was revealed stays
 * revealed for this opening. Hidden messages are not mounted, so they fetch
 * nothing; the conversation verbs still act on every member.
 * - **Conversation verbs** (`onArchive`, `onDelete`, `onSpam`, `onMoveTo`,
 *   `onToggleStar`, `onMarkUnread`, `onAddToTasks`) receive the conversation's
 *   IN-FOLDER members (`ConversationMessage[]`, oldest first) and the whole
 *   `ConversationDetail`, so the caller issues ONE batched request and shows
 *   ONE Undo. A merged Sent message (`inFolder: false`) is never among the
 *   members: archiving the Inbox conversation must not reach into Sent.
 *   Absent optional verbs draw no control, as in `ReadingPane`.
 * - **Per-message verbs**: `onReply(message, mode)` with the message the
 *   button belongs to (its `MessageDetail` once loaded, else its summary);
 *   `onBlockSender(message)` from that message's ⋮. Print, View headers and
 *   View source are handled here.
 * - `onMarkRead(messages)` — called ONCE with every expanded unread message,
 *   under the "Mark as read after N seconds" preference
 *   (`useConversationMarkRead`). A caller that marks on open must not pass it.
 * - Remote content: each expanded message keeps its own "Load images" choice
 *   (part of its detail query key, as in the flat pane); `isJunk` and
 *   `remoteRules` are passed through to every body.
 * - Summarize (Shift+S) is owned here — `useAiSummary` on the conversation's
 *   newest member with `conversation: true`. The caller must not also run
 *   `useAiSummary` on the open message while this pane is shown, or Shift+S
 *   is bound twice.
 */

import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConversationDetail,
  ConversationMessage,
  Label,
  MessageDetail,
  MessageSummary,
  RemoteContentRule,
  RemoteMode,
} from '@/api';
import { ApiError } from '@/api/http';
import { useApi } from '@/api/provider';
import { messageDetailStaleTime } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import { AiSummaryButton, useAiSummary } from '@/features/ai-summary';
import { AiSummaryPanel } from '@/app/lazyUi';
import { AddressContactMenu } from '@/features/contacts/AddressContactMenu';
import { defaultExpanded, inFolderMembers } from '@/features/conversation/expansion';
import { baseSubject, revealBatch, stackEntries } from '@/features/conversation/stack';
import { useConversation } from '@/features/conversation/useConversation';
import { useConversationMarkRead } from '@/features/conversation/useConversationMarkRead';
import type { FrameFit } from '@/features/message-frame/frameDocument';
import {
  defaultSubresourceFetcher,
  type SubresourceFetcher,
} from '@/features/message-frame/subresources';
import { Icon } from '@/components/Icon';
import { Sheet } from '@/components/Sheet';
import { ReadingPaneSkeleton, Skeleton } from '@/components/Skeleton';
import { useHandheld } from '@/hooks/useMediaQuery';
import { NotAvailableOffline } from '@/offline/OfflineNotices';
import { initialsOf } from '@/app/accounts';
import { cssVars } from '@/utils/css';
import { displayName, formatFullDate, formatListDate } from '@/utils/format';
import { failureCopy, formatAddress, MessageBodySection, MessageToolsMenu } from './ReadingPane';
import { HeaderLabelChips } from '@/features/labels/LabelChips';
import { labelsForKeywords } from '@/features/labels/keywords';
import { sameMessagePlaceholder } from './sameMessagePlaceholder';
import { useUnsubscribe, type UnsubscribeComposeSeed } from '@/features/unsubscribe/useUnsubscribe';
import './conversation-pane.css';

const ViewSourcePanel = lazy(() => import('@/features/view-source/ViewSourcePanel'));
const MessageHeadersPanel = lazy(
  () => import('@/features/message-headers/MessageHeadersPanel'),
);

export type ReplyMode = 'reply' | 'reply-all' | 'forward';

/** A conversation verb: the in-folder members, oldest first, and the conversation. */
export type ConversationAction = (
  members: ConversationMessage[],
  conversation: ConversationDetail,
) => void;

export interface ConversationPaneProps {
  /** The account the opened message belongs to. */
  accountId: string;
  /** The opened message: its conversation is shown, and it is expanded and scrolled to. */
  messageId: string;
  /** The phone form (ADR 0048). Defaults to the handheld breakpoint. */
  handheld?: boolean;
  /** Draw ← (default true). */
  showBack?: boolean;
  onBack: () => void;

  /* ------------------------------------------ conversation verbs (in-folder) */

  /** Archive (e). Absent: the mailbox has nowhere to archive to, and no button. */
  onArchive?: ConversationAction | undefined;
  /** Delete (Del). */
  onDelete: ConversationAction;
  /** Report spam, or Not spam when `isJunk` (!). Absent, no control. */
  onSpam?: ConversationAction | undefined;
  /** Move to…. Absent, no control. */
  onMoveTo?: ConversationAction | undefined;
  /** Star or unstar: `star` is the state asked for (the conversation is starred iff any member is). */
  onToggleStar: (members: ConversationMessage[], star: boolean, conversation: ConversationDetail) => void;
  /** Mark unread. Also stops a pending mark-as-read. Absent, no control. */
  onMarkUnread?: ConversationAction | undefined;
  /** Add to Tasks (Shift+T); the task is about `conversation.conversation.newestMessageId`. */
  onAddToTasks?: ConversationAction | undefined;
  /** Snooze the conversation (ADR 0076, b). Absent, no control. */
  onSnooze?: ConversationAction | undefined;

  /* ------------------------------------------------------ per-message verbs */

  /** Reply, Reply all or Forward to one message (the bottom pills: the newest). */
  onReply: (message: MessageDetail | ConversationMessage, mode: ReplyMode) => void;
  /** The message a server-prepared forward is in flight for: its Forward says so and is disabled. */
  forwardingId?: string | undefined;
  /** A refusal to show on the message it was about (e.g. a failed forward). */
  actionProblem?: { messageId: string; text: string } | undefined;
  /** "Block sender…" in a message's ⋮. Absent, no item. */
  onBlockSender?: ((message: MessageDetail) => void) | undefined;
  /**
   * Mark the expanded unread messages read: one call per batch, after the
   * owner's delay (see `useConversationMarkRead`).
   */
  onMarkRead?: ((messages: MessageSummary[]) => void) | undefined;
  /** A `mailto:` link in a body was activated. Absent, it stays inert. */
  onMailto?: ((href: string) => void) | undefined;
  /**
   * A `mailto:` unsubscribe: open a prefilled composer, as the flat pane
   * does. Absent, a mailto-only list offers no Unsubscribe (one-click and
   * link unsubscribes need nothing from the caller).
   */
  onComposeUnsubscribe?: ((seed: UnsubscribeComposeSeed) => void) | undefined;
  /** "Open contact" from a sender's menu. Absent, no item. */
  onOpenContact?: ((contactId: string) => void) | undefined;
  /** The caller's "In Tasks" chip, drawn under the subject. */
  taskChip?: ReactNode;
  /** "Label as" for the whole conversation (plan/17, keyboard `l`). Absent, no button. */
  onLabel?: ConversationAction | undefined;
  /**
   * The mailbox's labels, to turn the conversation's keywords into chips.
   * Absent, no chips.
   */
  labels?: readonly Label[] | undefined;
  /** A chip's ×: take the label off every message of the conversation. */
  onRemoveLabel?: ((label: Label, conversation: ConversationDetail) => void) | undefined;

  /* ---------------------------------------------------------- remote content */

  /** True in the Junk role folder: Not spam, and no persisted remote-content rules. */
  isJunk?: boolean;
  remoteRules?: readonly RemoteContentRule[];
  /** Tests only; must be a stable reference. */
  fetchSubresource?: SubresourceFetcher;
}

/**
 * Every label keyword of the conversation: its messages' own (they are what a
 * chip × and a live frame patch) plus the summary's union, which also covers
 * members older than the newest 100.
 */
function conversationKeywords(detail: ConversationDetail): string[] {
  const out: string[] = [];
  const add = (keyword: string) => {
    if (!out.some((held) => held.toLowerCase() === keyword.toLowerCase())) out.push(keyword);
  };
  for (const message of detail.messages) for (const keyword of message.labelKeywords ?? []) add(keyword);
  if (detail.omittedCount > 0) for (const keyword of detail.conversation.labelKeywords ?? []) add(keyword);
  return out;
}

/** The latest value, readable from a stable callback. */
function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function ConversationPane(props: ConversationPaneProps) {
  const {
    accountId,
    messageId,
    handheld: handheldProp,
    showBack = true,
    onBack,
    onArchive,
    onDelete,
    onSpam,
    onMoveTo,
    onToggleStar,
    onMarkUnread,
    onAddToTasks,
    onSnooze,
    onBlockSender,
    onMarkRead,
    forwardingId,
    actionProblem,
    taskChip,
    onLabel,
    labels,
    onRemoveLabel,
    isJunk = false,
  } = props;
  const handheldQuery = useHandheld();
  const handheld = handheldProp ?? handheldQuery;
  const queryClient = useQueryClient();

  const query = useConversation(accountId, messageId);
  const data = query.data;
  /** The previous conversation is still on screen while this one loads. */
  const stale = query.isPlaceholderData;
  const openingKey = `${accountId}\0${messageId}`;

  /*
   * Expansion. Until the reader toggles something, the default rule applies
   * (newest + unread + opened) — recomputed as the conversation changes, so a
   * new unread arrival opens expanded. Once they toggle, their choice is kept
   * for this opening, and opening another message starts over.
   */
  const [override, setOverride] = useState<{ key: string; ids: ReadonlySet<string> } | undefined>(
    undefined,
  );
  /*
   * The default only ever GROWS within one opening. Recomputing it from
   * scratch would collapse a message the moment mark-as-read lands (it is no
   * longer unread), closing what the reader is in the middle of reading —
   * which is exactly what the first live check showed.
   */
  /*
   * And it grows only by ARRIVALS: the rule is applied to the conversation as
   * it was when opened, then to each message that arrives while it is open.
   * Re-applying it to the whole conversation would cascade: reading the
   * newest five unread (MAX_AUTO_EXPANDED_UNREAD) makes the next five the
   * "newest unread", which open, are read, and so on through a chain of a
   * hundred unread notices — the first live check of the cap did exactly that.
   */
  const [auto, setAuto] = useState<
    { key: string; ids: ReadonlySet<string>; known: ReadonlySet<string> } | undefined
  >(undefined);
  if (data !== undefined && !stale) {
    // Adjusting state while rendering (React's "information from previous
    // renders" pattern): guarded, so it settles in one extra pass.
    const current = auto !== undefined && auto.key === openingKey ? auto : undefined;
    if (current === undefined) {
      setAuto({
        key: openingKey,
        ids: defaultExpanded(data, messageId),
        known: new Set(data.messages.map((message) => message.id)),
      });
    } else {
      const arrived = data.messages.filter((message) => !current.known.has(message.id));
      if (arrived.length > 0) {
        setAuto({
          key: openingKey,
          ids: new Set([...current.ids, ...defaultExpanded({ ...data, messages: arrived }, undefined)]),
          known: new Set([...current.known, ...arrived.map((message) => message.id)]),
        });
      }
    }
  }
  const expanded = useMemo<ReadonlySet<string>>(
    () =>
      override !== undefined && override.key === openingKey
        ? override.ids
        : auto !== undefined && auto.key === openingKey && !stale
          ? auto.ids
          : data !== undefined
            ? defaultExpanded(data, messageId)
            : new Set<string>(),
    [auto, data, messageId, openingKey, override, stale],
  );
  const expandedRef = useLatest(expanded);
  const toggle = useCallback(
    (id: string) => {
      const next = new Set(expandedRef.current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setOverride({ key: openingKey, ids: next });
    },
    [expandedRef, openingKey],
  );
  const allExpanded = data !== undefined && data.messages.every((message) => expanded.has(message.id));

  /*
   * Revealed: the middle messages a divider gave up. Like `auto`, it only
   * grows within one opening, so nothing the reader has seen goes back behind
   * a divider when it is marked read or collapsed.
   */
  const [revealed, setRevealed] = useState<{ key: string; ids: ReadonlySet<string> } | undefined>(undefined);
  const revealedIds = revealed !== undefined && revealed.key === openingKey ? revealed.ids : undefined;
  const reveal = useCallback(
    (ids: readonly string[]) =>
      setRevealed((current) => ({
        key: openingKey,
        ids: new Set([...(current !== undefined && current.key === openingKey ? current.ids : []), ...ids]),
      })),
    [openingKey],
  );
  const entries = useMemo(() => {
    if (data === undefined) return [];
    const shown = new Set([...expanded, ...(revealedIds ?? [])]);
    if (auto !== undefined && auto.key === openingKey && !stale) for (const id of auto.ids) shown.add(id);
    return stackEntries(data.messages, shown);
  }, [auto, data, expanded, openingKey, revealedIds, stale]);
  /* A divider that reveals its last batch goes away: focus the first message it gave up. */
  const focusAfterReveal = useRef<string | undefined>(undefined);

  const expandOrCollapseAll = () => {
    if (data === undefined) return;
    // "Expand all" is an explicit ask for everything: every divider opens too.
    // Bodies stay lazy all the same, one detail query per expanded message.
    if (!allExpanded) reveal(data.messages.map((message) => message.id));
    setOverride({
      key: openingKey,
      ids: allExpanded
        ? // "Collapse all" keeps the newest open: a stack of one-liners with
          // nothing to read is not a reading pane.
          new Set(data.messages.slice(-1).map((message) => message.id))
        : new Set(data.messages.map((message) => message.id)),
    });
  };

  /* Mark as read: the expanded messages of THIS conversation, never the placeholder's. */
  /*
   * ...and only once their bodies are on screen. Marking read is a flag
   * mutation, and every flag mutation cancels in-flight message queries
   * (cancelAffectedQueries), so marking before the bodies arrived aborted the
   * very fetch that shows them and paid for it twice. The flat pane never hit
   * this: it marks the message whose detail it already has.
   */
  const bodiesFetching = useIsFetching({
    predicate: (query) =>
      query.queryKey[0] === 'account' &&
      query.queryKey[1] === accountId &&
      query.queryKey[2] === 'message' &&
      typeof query.queryKey[3] === 'string' &&
      expanded.has(query.queryKey[3]),
  });
  const expandedMessages = useMemo(() => {
    if (data === undefined || stale || bodiesFetching > 0) return undefined;
    const shown = data.messages.filter((message) => expanded.has(message.id));
    // Not fetching is not enough: on the first render with the conversation
    // the bodies' queries have not even started. `bodiesFetching` changing is
    // what re-runs this once they land.
    const loaded = shown.every(
      (message) =>
        queryClient
          .getQueriesData({ queryKey: queryKeys.account(accountId).message(message.id) })
          .some(([, value]) => value !== undefined),
    );
    return loaded ? shown : undefined;
  }, [accountId, data, expanded, stale, bodiesFetching, queryClient]);
  const markRead = useConversationMarkRead(
    data === undefined || stale ? undefined : openingKey,
    expandedMessages,
    onMarkRead,
  );

  /* Summarize the whole conversation (ADR 0074 + 0077), from its newest member. */
  const newestId = data !== undefined && !stale ? data.conversation.newestMessageId : undefined;
  const aiSummary = useAiSummary(
    newestId !== undefined ? { id: newestId, accountId } : undefined,
    { conversation: true },
  );
  const summary = aiSummary.available ? aiSummary : undefined;

  /* Per-message remote-content choice: part of that message's detail key. */
  const [remoteModes, setRemoteModes] = useState<{ key: string; modes: Record<string, RemoteMode> }>({
    key: openingKey,
    modes: {},
  });
  const modes = remoteModes.key === openingKey ? remoteModes.modes : undefined;
  const setRemoteMode = useCallback(
    (id: string, mode: RemoteMode | undefined) =>
      setRemoteModes((current) => {
        const base = current.key === openingKey ? { ...current.modes } : {};
        if (mode === undefined) delete base[id];
        else base[id] = mode;
        return { key: openingKey, modes: base };
      }),
    [openingKey],
  );

  /* Headers and source panels, for one message at a time. */
  const [panel, setPanel] = useState<{ kind: 'headers' | 'source'; message: MessageDetail } | undefined>(
    undefined,
  );
  const [sheetFor, setSheetFor] = useState<string | undefined>(undefined);
  const sheetOpen = sheetFor === openingKey;

  /* Stable per-message callbacks, so expanding one message re-renders only it. */
  const onReplyRef = useLatest(props.onReply);
  const reply = useCallback(
    (message: MessageDetail | ConversationMessage, mode: ReplyMode) => onReplyRef.current(message, mode),
    [onReplyRef],
  );
  const onBlockSenderRef = useLatest(onBlockSender);
  const blockSender = useCallback(
    (message: MessageDetail) => onBlockSenderRef.current?.(message),
    [onBlockSenderRef],
  );
  const openPanel = useCallback(
    (kind: 'headers' | 'source', message: MessageDetail) => setPanel({ kind, message }),
    [],
  );
  const onMailtoRef = useLatest(props.onMailto);
  const mailto = useCallback((href: string) => onMailtoRef.current?.(href), [onMailtoRef]);
  const onComposeUnsubscribeRef = useLatest(props.onComposeUnsubscribe);
  const composeUnsubscribe = useCallback(
    (seed: UnsubscribeComposeSeed) => onComposeUnsubscribeRef.current?.(seed),
    [onComposeUnsubscribeRef],
  );
  const onOpenContactRef = useLatest(props.onOpenContact);
  const openContact = useCallback(
    (contactId: string) => onOpenContactRef.current?.(contactId),
    [onOpenContactRef],
  );

  /* The opened message, scrolled into view once per opening. */
  const stackRef = useRef<HTMLOListElement>(null);
  const scrolledFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (data === undefined || stale || scrolledFor.current === openingKey) return;
    scrolledFor.current = openingKey;
    // The first message needs no scroll: the subject above it stays in view.
    if (data.messages[0]?.id === messageId) return;
    const item = Array.from(stackRef.current?.querySelectorAll<HTMLElement>('[data-message-id]') ?? []).find(
      (element) => element.dataset.messageId === messageId,
    );
    item?.scrollIntoView?.({ block: 'start' });
  }, [data, messageId, openingKey, stale]);

  useEffect(() => {
    const id = focusAfterReveal.current;
    if (id === undefined) return;
    focusAfterReveal.current = undefined;
    Array.from(stackRef.current?.querySelectorAll<HTMLElement>('[data-message-id]') ?? [])
      .find((element) => element.dataset.messageId === id)
      ?.querySelector<HTMLElement>('.conversation__row')
      ?.focus();
  }, [revealedIds]);

  if (data === undefined) {
    if (query.error) {
      const error = query.error;
      if (error instanceof ApiError && error.isOffline) {
        return (
          <NotAvailableOffline
            what="message"
            onRetry={() => void query.refetch()}
            {...(showBack ? { onBack } : {})}
          />
        );
      }
      const copy = failureCopy(error);
      return (
        <div className="empty-state" role="alert">
          <Icon name="mail-unread" size={32} />
          <p className="empty-state__title">{copy.title}</p>
          <p className="muted">{copy.detail}</p>
          <div className="empty-state__actions">
            <button type="button" className="btn" onClick={onBack}>
              Back to list
            </button>
            {copy.retryable && (
              <button type="button" className="btn btn--primary" onClick={() => void query.refetch()}>
                Try again
              </button>
            )}
          </div>
        </div>
      );
    }
    return <ReadingPaneSkeleton />;
  }

  const conversation = data.conversation;
  const members = inFolderMembers(data);
  const starred = members.some((message) => message.flags.includes('flagged'));
  const inSent = conversation.labels.some((label) => label.role === 'sent');
  const meEmails = new Set(
    conversation.participants
      .filter((participant) => participant.isMe)
      .map((participant) => participant.email.toLowerCase()),
  );
  const newest = data.messages.at(-1);
  // Verbs act on what is on screen; while the next conversation loads, they wait.
  const act = (action: ConversationAction | undefined) =>
    action === undefined ? undefined : () => {
      if (!stale) action(members, data);
    };
  const archive = act(onArchive);
  const remove = act(onDelete);
  const spam = act(onSpam);
  const moveTo = act(onMoveTo);
  const addToTasks = act(onAddToTasks);
  const snooze = act(onSnooze);
  const labelAs = act(onLabel);
  // The conversation's labels: the union over every message on screen, which
  // is what an optimistic chip × or a live frame patches (plan/17 §5).
  const conversationLabels = labelsForKeywords(conversationKeywords(data), labels);
  const markUnread =
    onMarkUnread &&
    (() => {
      if (stale) return;
      // The reader's choice outranks a countdown that has not fired yet.
      markRead.cancel();
      onMarkUnread(members, data);
    });
  const toggleStar = () => {
    if (!stale) onToggleStar(members, !starred, data);
  };
  const fromSheet = (action: (() => void) | undefined) => () => {
    setSheetFor(undefined);
    action?.();
  };
  const replyToNewest = (mode: ReplyMode) => {
    if (newest === undefined || stale) return;
    // Its detail when it is loaded (it is expanded by default), else its summary.
    const detail = queryClient.getQueryData<MessageDetail>(
      queryKeys.account(accountId).message(newest.id, modes?.[newest.id]),
    );
    reply(detail ?? newest, mode);
  };
  const forwardBusy = forwardingId !== undefined && forwardingId === newest?.id;

  return (
    <>
      {handheld ? (
        <div className="reading-actions reading-actions--appbar">
          {showBack && (
            <button type="button" className="btn btn--icon pane__back" onClick={onBack} aria-label="Back to list">
              <Icon name="chevron-left" />
            </button>
          )}
          <span className="spacer" />
          {archive && (
            <button type="button" className="btn btn--icon" onClick={archive} aria-label="Archive">
              <Icon name="archive" />
            </button>
          )}
          <button type="button" className="btn btn--icon" onClick={remove} aria-label="Delete">
            <Icon name="trash" />
          </button>
          {markUnread && (
            <button type="button" className="btn btn--icon" onClick={markUnread} aria-label="Mark unread">
              <Icon name="mail-unread" />
            </button>
          )}
          <button
            type="button"
            className="btn btn--icon"
            onClick={toggleStar}
            aria-pressed={starred}
            aria-label={starred ? 'Remove star' : 'Add star'}
          >
            <Icon name={starred ? 'star-filled' : 'star'} />
          </button>
          <button
            type="button"
            className="btn btn--icon"
            onClick={() => setSheetFor(openingKey)}
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            aria-label="More actions"
          >
            <Icon name="more" />
          </button>
        </div>
      ) : (
        <div className="reading-actions">
          {showBack && (
            <button type="button" className="btn btn--icon pane__back" onClick={onBack} aria-label="Back to list">
              <Icon name="chevron-left" />
            </button>
          )}
          {archive && (
            <button type="button" className="btn btn--icon" onClick={archive} aria-label="Archive (e)" title="Archive (e)">
              <Icon name="archive" />
            </button>
          )}
          <button type="button" className="btn btn--icon" onClick={remove} aria-label="Delete (Del)" title="Delete (Del)">
            <Icon name="trash" />
          </button>
          {spam && (
            <button
              type="button"
              className="btn btn--icon"
              onClick={spam}
              aria-label={isJunk ? 'Not spam (!)' : 'Report spam (!)'}
              title={isJunk ? 'Not spam (!)' : 'Report spam (!)'}
            >
              <Icon name={isJunk ? 'inbox' : 'junk'} />
            </button>
          )}
          {moveTo && (
            <button type="button" className="btn btn--icon" onClick={moveTo} aria-label="Move to…" title="Move to…">
              <Icon name="folder" />
            </button>
          )}
          {markUnread && (
            <button type="button" className="btn btn--icon" onClick={markUnread} aria-label="Mark unread" title="Mark unread">
              <Icon name="mail-unread" />
            </button>
          )}
          <button
            type="button"
            className="btn btn--icon"
            onClick={toggleStar}
            aria-pressed={starred}
            aria-label={starred ? 'Remove star (s)' : 'Add star (s)'}
          >
            <Icon name={starred ? 'star-filled' : 'star'} />
          </button>
          {addToTasks && (
            <button
              type="button"
              className="btn btn--icon"
              onClick={addToTasks}
              aria-label="Add to Tasks (Shift+T)"
              title="Add to Tasks (Shift+T)"
            >
              <Icon name="tasks" />
            </button>
          )}
          {snooze && (
            <button
              type="button"
              className="btn btn--icon"
              onClick={snooze}
              aria-label="Snooze (b)"
              title="Snooze (b)"
            >
              <Icon name="clock" />
            </button>
          )}
          {labelAs && (
            <button
              type="button"
              className="btn btn--icon"
              onClick={labelAs}
              aria-haspopup="dialog"
              aria-label="Label as (l)"
              title="Label as (l)"
            >
              <Icon name="label" />
            </button>
          )}
          <span className="spacer" />
          {summary && <AiSummaryButton summary={summary} />}
        </div>
      )}

      <div
        className={handheld ? 'pane__scroll reading-scroll--handheld conversation' : 'pane__scroll conversation'}
        aria-busy={stale}
      >
        <header className="reading-header conversation__header">
          {/* h2 under the list's h1; each message below is an h3 (#205). */}
          <h2 className="reading-subject" dir="auto">
            {conversation.subject || '(no subject)'}
          </h2>
          <div className="conversation__facts">
            <span>{plural(Math.max(conversation.messageCount, data.messages.length), 'message', 'messages')}</span>
            {inSent && <span className="badge">Sent</span>}
            <span className="spacer" />
            {data.messages.length > 1 && (
              <button type="button" className="btn btn--ghost conversation__expand-all" onClick={expandOrCollapseAll}>
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>
          {taskChip}
          <HeaderLabelChips
            labels={conversationLabels}
            scope="conversation"
            onRemove={
              onRemoveLabel
                ? (label) => {
                    if (!stale) onRemoveLabel(label, data);
                  }
                : undefined
            }
          />
        </header>

        {summary && <AiSummaryPanel summary={summary} />}

        <ol className="conversation__stack" aria-label="Messages in this conversation" ref={stackRef}>
          {data.omittedCount > 0 && (
            <li className="conversation__omitted">
              {plural(data.omittedCount, 'older message', 'older messages')} not shown
            </li>
          )}
          {entries.map((entry, index) => {
            if (entry.kind === 'hidden') {
              const run = entry.messages;
              const batch = revealBatch(run);
              // Keyed by the message above, which a reveal (from the bottom of
              // the run) never changes: the button keeps focus across clicks.
              const above = entries[index - 1];
              const key = above?.kind === 'message' ? `after-${above.message.id}` : `before-${run[0]!.id}`;
              const count = plural(run.length, 'older message', 'older messages');
              return (
                <li key={key} className="conversation__divider">
                  <button
                    type="button"
                    className="conversation__more"
                    aria-expanded={false}
                    aria-label={batch.length < run.length ? `Show ${batch.length} of ${count}` : `Show ${count}`}
                    onClick={() => {
                      if (batch.length === run.length) focusAfterReveal.current = run[0]!.id;
                      reveal(batch.map((message) => message.id));
                    }}
                  >
                    <span className="conversation__more-count">{count}</span>
                  </button>
                </li>
              );
            }
            const message = entry.message;
            const subject =
              message.subject !== '' && baseSubject(message.subject) !== baseSubject(conversation.subject)
                ? message.subject
                : undefined;
            return (
              <ConversationItem
                key={message.id}
                message={message}
                subject={subject}
                accountId={accountId}
                expanded={expanded.has(message.id)}
                isMe={!message.inFolder || meEmails.has(message.from.email.toLowerCase())}
                handheld={handheld}
                onToggle={toggle}
                onReply={reply}
                forwardBusy={forwardingId !== undefined && forwardingId === message.id}
                problem={actionProblem?.messageId === message.id ? actionProblem.text : undefined}
                onPanel={openPanel}
                onBlockSender={onBlockSender ? blockSender : undefined}
                onMailto={props.onMailto ? mailto : undefined}
                onComposeUnsubscribe={props.onComposeUnsubscribe ? composeUnsubscribe : undefined}
                onOpenContact={props.onOpenContact ? openContact : undefined}
                remoteMode={modes?.[message.id]}
                onRemoteModeChange={setRemoteMode}
                isJunk={isJunk}
                remoteRules={props.remoteRules}
                fetchSubresource={props.fetchSubresource ?? defaultSubresourceFetcher}
              />
            );
          })}
        </ol>

        {/* Where reading ends: answer the newest message (plan/16 §8). */}
        <div className="reading-replies conversation__replies" role="group" aria-label="Reply to the newest message">
          <button type="button" className="btn reading-replies__pill" onClick={() => replyToNewest('reply')}>
            <Icon name="reply" /> Reply
          </button>
          <button type="button" className="btn reading-replies__pill" onClick={() => replyToNewest('reply-all')}>
            <Icon name="reply-all" /> Reply all
          </button>
          <button
            type="button"
            className="btn reading-replies__pill"
            onClick={() => replyToNewest('forward')}
            disabled={forwardBusy}
          >
            <Icon name="forward" /> {forwardBusy ? 'Forwarding…' : 'Forward'}
          </button>
        </div>
      </div>

      {handheld && (
        <Sheet open={sheetOpen} onClose={() => setSheetFor(undefined)} title="Conversation actions" hideTitle>
          <div className="reading-sheet">
            {summary && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => summary.summarize())}>
                Summarize with AI
              </button>
            )}
            {addToTasks && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(addToTasks)}>
                Add to Tasks
              </button>
            )}
            {snooze && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(snooze)}>
                Snooze
              </button>
            )}
            {labelAs && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(labelAs)}>
                Label as…
              </button>
            )}
            {moveTo && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(moveTo)}>
                Move to…
              </button>
            )}
            {spam && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(spam)}>
                {isJunk ? 'Not spam' : 'Report spam'}
              </button>
            )}
            {data.messages.length > 1 && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(expandOrCollapseAll)}>
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>
        </Sheet>
      )}

      {panel?.kind === 'headers' && (
        <Suspense fallback={null}>
          <MessageHeadersPanel accountId={accountId} message={panel.message} onClose={() => setPanel(undefined)} />
        </Suspense>
      )}
      {panel?.kind === 'source' && (
        <Suspense fallback={null}>
          <ViewSourcePanel accountId={accountId} message={panel.message} onClose={() => setPanel(undefined)} />
        </Suspense>
      )}
    </>
  );
}

/* ------------------------------------------------------------ one message */

interface ConversationItemProps {
  message: ConversationMessage;
  /** Its subject, when it says something the conversation's does not: shown on the collapsed row. */
  subject?: string | undefined;
  accountId: string;
  expanded: boolean;
  /** Sent by the account's owner: shown as "me". */
  isMe: boolean;
  handheld: boolean;
  onToggle: (id: string) => void;
  onReply: (message: MessageDetail | ConversationMessage, mode: ReplyMode) => void;
  forwardBusy: boolean;
  problem: string | undefined;
  onPanel: (kind: 'headers' | 'source', message: MessageDetail) => void;
  onBlockSender: ((message: MessageDetail) => void) | undefined;
  onMailto: ((href: string) => void) | undefined;
  onComposeUnsubscribe: ((seed: UnsubscribeComposeSeed) => void) | undefined;
  onOpenContact: ((contactId: string) => void) | undefined;
  remoteMode: RemoteMode | undefined;
  onRemoteModeChange: (id: string, mode: RemoteMode | undefined) => void;
  isJunk: boolean;
  remoteRules: readonly RemoteContentRule[] | undefined;
  fetchSubresource: SubresourceFetcher;
}

/**
 * One message of the stack. Memoised: every prop is a primitive, the
 * message object (kept by the query's structural sharing) or a stable
 * callback, so toggling one message re-renders that one only.
 *
 * The heading's button is the toggle for both states (Enter and Space come
 * with `<button>`), so focus stays where it was when a message opens or
 * closes and a screen reader hears the new `aria-expanded`.
 */
const ConversationItem = memo(function ConversationItem({
  message,
  subject,
  accountId,
  expanded,
  isMe,
  handheld,
  onToggle,
  ...rest
}: ConversationItemProps) {
  const unread = !message.flags.includes('seen');
  const sender = isMe ? 'me' : displayName(message.from);
  const bodyId = `conversation-body-${message.id}`;

  return (
    <li
      className={expanded ? 'conversation__item conversation__item--expanded' : 'conversation__item'}
      data-message-id={message.id}
    >
      <h3 className="conversation__heading">
        <button
          type="button"
          className="conversation__row"
          aria-expanded={expanded}
          {...(expanded ? { 'aria-controls': bodyId } : {})}
          data-unread={unread ? '' : undefined}
          onClick={() => onToggle(message.id)}
        >
          <span className="avatar" style={cssVars({ '--avatar-color': 'var(--color-accent)', '--avatar-ink': 'var(--color-text-on-accent)' })} aria-hidden="true">
            {initialsOf(isMe ? 'me' : displayName(message.from))}
          </span>
          <span className="conversation__from truncate">{sender}</span>
          {unread && <span className="visually-hidden">, unread</span>}
          {!message.inFolder && <span className="badge conversation__sent">Sent</span>}
          {!expanded && (
            <span className="conversation__snippet truncate">
              {subject !== undefined && <span className="conversation__subject" dir="auto">
                  {subject}
                </span>}
              {subject !== undefined && message.snippet !== '' && ' — '}
              {message.snippet}
            </span>
          )}
          {expanded && <span className="spacer" />}
          {message.hasAttachments && (
            <span className="conversation__clip" title="Has attachments">
              <Icon name="attachment" size={16} />
              <span className="visually-hidden">, has attachments</span>
            </span>
          )}
          <span className="conversation__date">
            {expanded && !handheld ? formatFullDate(message.date) : formatListDate(message.date)}
          </span>
        </button>
      </h3>
      {expanded && (
        <ExpandedMessage message={message} accountId={accountId} handheld={handheld} bodyId={bodyId} {...rest} />
      )}
    </li>
  );
});

type ExpandedMessageProps = Omit<ConversationItemProps, 'expanded' | 'isMe' | 'onToggle' | 'subject'> & {
  bodyId: string;
};

/**
 * An expanded message: recipients, its own verbs, and the body from the
 * message detail query — the flat pane's key, so the two share one cache and
 * a message read in one mode is not fetched again in the other. Mounted only
 * while expanded: a collapsed message fetches nothing.
 */
function ExpandedMessage({
  message,
  accountId,
  handheld,
  bodyId,
  onReply,
  forwardBusy,
  problem,
  onPanel,
  onBlockSender,
  onMailto,
  onComposeUnsubscribe,
  onOpenContact,
  remoteMode,
  onRemoteModeChange,
  isJunk,
  remoteRules,
  fetchSubresource,
}: ExpandedMessageProps) {
  const api = useApi();
  const detailQuery = useQuery({
    queryKey: queryKeys.account(accountId).message(message.id, remoteMode),
    queryFn: ({ signal }) =>
      api.messages.get(accountId, message.id, { signal, ...(remoteMode ? { remote: remoteMode } : {}) }),
    // A delivered body is immutable; its flags are patched, never refetched.
    // What the "always load" rules made of its images is not.
    staleTime: messageDetailStaleTime(remoteMode),
    // Load images: keep this body on screen, not a skeleton.
    placeholderData: (previous) => sameMessagePlaceholder(previous, accountId, message.id),
  });
  const detail = detailQuery.data;
  // Unsubscribe beside the sender, as in the flat pane (ADR 0066): the
  // conversation view is the default, and it never offered one.
  const unsubscribe = useUnsubscribe({
    message: detail,
    accountId,
    isJunk,
    onCompose: onComposeUnsubscribe,
  });

  const [plainText, setPlainText] = useState(false);
  const [actualSize, setActualSize] = useState(false);
  const [wide, setWide] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const frameFit: FrameFit = handheld && actualSize ? 'actual' : 'width';
  const target = detail ?? message;
  const canViewSource = accountId !== '';
  const hasBothBodies =
    detail !== undefined &&
    detail.bodyHtml !== undefined &&
    detail.bodyHtml !== '' &&
    detail.bodyText !== undefined &&
    detail.bodyText !== '';
  const fromSheet = (action: () => void) => () => {
    setSheetOpen(false);
    action();
  };

  const addressTrigger = (address: MessageDetail['from'], label: string): ReactNode =>
    accountId === '' ? (
      <span>{label}</span>
    ) : (
      <AddressContactMenu
        accountId={accountId}
        address={address}
        handheld={handheld}
        {...(onOpenContact !== undefined ? { onOpenContact } : {})}
      >
        {label}
      </AddressContactMenu>
    );
  const recipients = detail ?? message;
  const cc = detail?.cc ?? [];

  return (
    <div className="conversation__message" id={bodyId}>
      <div className="conversation__meta">
        <div className="conversation__addresses">
          <div className="conversation__line">
            {addressTrigger(message.from, formatAddress(message.from))}
            {unsubscribe.trigger}
          </div>
          <div className="conversation__line truncate">
            to {recipients.to.length > 0 ? recipients.to.map(displayName).join(', ') : 'me'}
            {cc.length > 0 && ` · cc ${cc.map(displayName).join(', ')}`}
          </div>
        </div>
        <div className="conversation__actions">
          {handheld ? (
            <>
              <button type="button" className="btn btn--icon" onClick={() => onReply(target, 'reply')} aria-label="Reply">
                <Icon name="reply" />
              </button>
              <button
                type="button"
                className="btn btn--icon"
                onClick={() => setSheetOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={sheetOpen}
                aria-label="Message actions"
              >
                <Icon name="more" />
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn--icon" onClick={() => onReply(target, 'reply')} aria-label="Reply" title="Reply">
                <Icon name="reply" />
              </button>
              <button
                type="button"
                className="btn btn--icon"
                onClick={() => onReply(target, 'reply-all')}
                aria-label="Reply all"
                title="Reply all"
              >
                <Icon name="reply-all" />
              </button>
              <button
                type="button"
                className="btn btn--icon"
                onClick={() => onReply(target, 'forward')}
                disabled={forwardBusy}
                aria-label={forwardBusy ? 'Forwarding…' : 'Forward'}
                title={forwardBusy ? 'Forwarding…' : 'Forward'}
              >
                <Icon name="forward" />
              </button>
              {detail !== undefined && (
                <MessageToolsMenu
                  label="Message actions"
                  canViewSource={canViewSource}
                  onHeaders={() => onPanel('headers', detail)}
                  onSource={() => onPanel('source', detail)}
                  onBlockSender={onBlockSender && (() => onBlockSender(detail))}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Unsubscribe's confirm or result, or why it is not offered here. */}
      {unsubscribe.panel}

      {problem !== undefined && (
        <p className="reading-notice reading-notice--failed" role="alert">
          {problem}
        </p>
      )}

      {detail !== undefined ? (
        <MessageBodySection
          message={detail}
          handheld={handheld}
          showPlainText={plainText}
          onTogglePlainText={() => setPlainText((value) => !value)}
          frameFit={frameFit}
          onWideChange={setWide}
          accountId={accountId}
          remoteMode={remoteMode}
          onRemoteModeChange={(mode) => onRemoteModeChange(message.id, mode)}
          isJunk={isJunk}
          {...(remoteRules !== undefined ? { remoteRules } : {})}
          fetchSubresource={fetchSubresource}
          {...(onMailto !== undefined ? { onMailto } : {})}
        />
      ) : detailQuery.error ? (
        <p className="reading-notice reading-notice--failed" role="alert">
          {failureCopy(detailQuery.error).title}.{' '}
          <button
            type="button"
            className="btn btn--ghost reading-notice__retry"
            onClick={() => void detailQuery.refetch()}
          >
            Try again
          </button>
        </p>
      ) : (
        <div className="conversation__loading" aria-label="Loading message" role="status">
          <Skeleton width="80%" />
          <Skeleton width="65%" />
          <Skeleton width="72%" />
        </div>
      )}

      {handheld && (
        <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Message actions" hideTitle>
          <div className="reading-sheet">
            <button type="button" className="reading-sheet__item" onClick={fromSheet(() => onReply(target, 'reply'))}>
              Reply
            </button>
            <button type="button" className="reading-sheet__item" onClick={fromSheet(() => onReply(target, 'reply-all'))}>
              Reply all
            </button>
            <button
              type="button"
              className="reading-sheet__item"
              onClick={fromSheet(() => onReply(target, 'forward'))}
              disabled={forwardBusy}
            >
              {forwardBusy ? 'Forwarding…' : 'Forward'}
            </button>
            {hasBothBodies && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => setPlainText((value) => !value))}>
                {plainText ? 'View formatted' : 'View as plain text'}
              </button>
            )}
            {wide && !plainText && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => setActualSize((value) => !value))}>
                {actualSize ? 'Fit to width' : 'Actual size'}
              </button>
            )}
            <button type="button" className="reading-sheet__item" onClick={fromSheet(() => window.print())}>
              Print
            </button>
            {canViewSource && detail !== undefined && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => onPanel('headers', detail))}>
                View headers
              </button>
            )}
            {canViewSource && detail !== undefined && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => onPanel('source', detail))}>
                View source
              </button>
            )}
            {onBlockSender && detail !== undefined && (
              <button type="button" className="reading-sheet__item" onClick={fromSheet(() => onBlockSender(detail))}>
                Block sender…
              </button>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
}
